import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Onboarding = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    age: '',
    weight: '',
    height: '',
    gender: '',
    fitness_level: '',
    goal: '',
    dietary_restrictions: [],
    excluded_foods: []
  });
  const [loading, setLoading] = useState(false);

  // 2026-02 · Validación de estatura: debe estar en centímetros (>= 100).
  const heightNum = parseFloat(formData.height);
  const heightInvalid = formData.height !== '' && (!isNaN(heightNum) && heightNum < 100);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (heightInvalid) {
      toast.error('La estatura debe estar en centímetros (ejemplo: 179)');
      return;
    }
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/complete-profile`, {
        ...formData,
        age: parseInt(formData.age),
        weight: parseFloat(formData.weight),
        height: parseFloat(formData.height)
      }, { withCredentials: false });
      
      toast.success('¡Perfil completado! Ahora puedes explorar todos los servicios');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Error al guardar el perfil');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen py-12 px-4 md:px-6 relative"
      style={{ backgroundColor: '#0a0a0a' }}
      data-testid="onboarding-page"
    >
      {/* Mesh gradient background */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 20% 80%, rgba(var(--brand-primary-glow), 0.12), transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 240, 255, 0.08), transparent 40%)'
        }}
      />
      
      <div className="relative z-10 max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div 
            className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6"
            style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
          >
            <span className="text-4xl">🎉</span>
          </div>
          <h1 
            className="text-4xl md:text-5xl font-bold text-white mb-4 uppercase tracking-tight"
            style={{ fontFamily: 'Poppins, sans-serif' }}
            data-testid="onboarding-title"
          >
            ¡Bienvenida!
          </h1>
          <p className="text-gray-400 text-lg">
            Completa tu perfil para obtener planes personalizados
          </p>
        </motion.div>

        {/* Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div 
            className="rounded-2xl backdrop-blur-xl p-8"
            style={{ 
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <div className="mb-6">
              <h2 
                className="text-xl font-bold text-white uppercase"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Información Personal
              </h2>
              <p className="text-gray-400 text-sm mt-1">Cuéntanos sobre ti para crear el mejor plan</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Edad *</Label>
                  <Input
                    type="number"
                    placeholder="25"
                    value={formData.age}
                    onChange={(e) => setFormData({...formData, age: e.target.value})}
                    required
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    data-testid="age-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Género *</Label>
                  <Select value={formData.gender} onValueChange={(val) => setFormData({...formData, gender: val})} required>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="gender-select">
                      <SelectValue placeholder="Selecciona" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      <SelectItem value="female" className="text-white hover:bg-white/10">Femenino</SelectItem>
                      <SelectItem value="male" className="text-white hover:bg-white/10">Masculino</SelectItem>
                      <SelectItem value="other" className="text-white hover:bg-white/10">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Peso (kg) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="60"
                    value={formData.weight}
                    onChange={(e) => setFormData({...formData, weight: e.target.value})}
                    required
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    data-testid="weight-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Altura (cm) *</Label>
                  <Input
                    type="number"
                    placeholder="165"
                    value={formData.height}
                    onChange={(e) => setFormData({...formData, height: e.target.value})}
                    required
                    aria-invalid={heightInvalid}
                    className={`bg-white/5 text-white placeholder:text-gray-500 ${heightInvalid ? 'border-red-500 focus:border-red-500' : 'border-white/10'}`}
                    data-testid="height-input"
                  />
                  {heightInvalid && (
                    <p className="text-sm font-semibold text-red-500 mt-1" data-testid="height-error">
                      Ingresa tu estatura en centímetros (ejemplo: 179)
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Nivel de Fitness *</Label>
                  <Select value={formData.fitness_level} onValueChange={(val) => setFormData({...formData, fitness_level: val})} required>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="fitness-level-select">
                      <SelectValue placeholder="Selecciona" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      <SelectItem value="beginner" className="text-white hover:bg-white/10">Principiante</SelectItem>
                      <SelectItem value="intermediate" className="text-white hover:bg-white/10">Intermedio</SelectItem>
                      <SelectItem value="advanced" className="text-white hover:bg-white/10">Avanzado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Objetivo *</Label>
                  <Select value={formData.goal} onValueChange={(val) => setFormData({...formData, goal: val})} required>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="goal-select">
                      <SelectValue placeholder="Selecciona" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      <SelectItem value="lose_weight" className="text-white hover:bg-white/10">Perder Peso</SelectItem>
                      <SelectItem value="gain_muscle" className="text-white hover:bg-white/10">Ganar Músculo</SelectItem>
                      <SelectItem value="maintain" className="text-white hover:bg-white/10">Mantener</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate('/dashboard')}
                  className="border-gray-600 text-gray-300 hover:bg-white/10"
                  data-testid="skip-button"
                >
                  Omitir
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 rounded-full py-6 font-bold uppercase tracking-wider text-white"
                  style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                  disabled={loading || heightInvalid}
                  data-testid="submit-button"
                >
                  {loading ? 'Guardando...' : 'Continuar'}
                </Button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Onboarding;
